import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { TextInput } from "@msuite/ui/form";
import { useDisclosure, useFilter, useSearchForm } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useMemo } from "react";
import { useCrewDefinitions } from "@/hooks/use-crew-definitions";
import { AddCrewDefinitionModal } from "@/modals/plan/add-crew-definition-modal";
import { spanReachesWindow, useCapacityCalendar } from "../capacity-calendar";
import { CrewDefinitionCard } from "./crew-definition-card";

/**
 * The standing crews an organisation plans with, and the place they are defined. The list is the
 * crews segment of the capacity resources panel — every other segment is still a placeholder.
 *
 * It shows the crews the board is showing: a crew whose span does not reach the visible weeks is
 * not one this view can plan, so the week picker moves the panel as well as the calendar. The
 * controller is a plain reader of the plan store, so the panel derives the same window without
 * standing inside the calendar.
 *
 * **The planner view is the only one that mounts this.** The manager view puts `ManagerSlotResources`
 * in the same segment, so defining a crew is confined to the board that plans crews without this
 * component testing for it.
 */
export function CrewDefinitionResources() {
	/** Hooks */
	const { t } = useTranslation();
	const searchForm = useSearchForm("capacity-crews-resources");
	const addModal = useDisclosure();
	const controller = useCapacityCalendar();

	/** Query */
	const { crewDefinitions, isLoading } = useCrewDefinitions();

	/** Computed */
	const inWindow = useMemo(() => {
		const result: typeof crewDefinitions = [];
		for (const crewDefinition of crewDefinitions) {
			if (!spanReachesWindow(crewDefinition, controller.windowStart, controller.windowEnd)) {
				continue;
			}
			result.push(crewDefinition);
		}
		return result;
	}, [crewDefinitions, controller.windowStart, controller.windowEnd]);

	const { filtered } = useFilter(inWindow, searchForm.searchValue);

	// A crew hidden by the window reads as a crew that does not exist, so the empty state names the
	// window rather than claiming there is nothing.
	const blankSlateLabel = crewDefinitions.length
		? t("planning.crews.no_crews_in_window")
		: undefined;

	/** Render */
	return (
		<ContentCard
			title={t("planning.crews.crews_title")}
			icon={icon("Users")}
			contentClassName="v-stack flex-1 min-h-0 overflow-y-auto"
			className="h-full overflow-hidden"
			variant="plain"
		>
			<div className="h-stack p-1">
				<div {...searchForm.getSearchInputWrapperProps()}>
					<TextInput {...searchForm.getSearchInputProps()} />
				</div>
				<Button
					icon={<Icon name="Plus" />}
					size="sm"
					onClick={addModal.onOpen}
				>
					{t("planning.crews.add_crew_definition")}
				</Button>
			</div>
			{isLoading && <BlankSlate isLoading />}
			{!isLoading && !filtered.length && (
				<BlankSlate
					withBorder
					label={blankSlateLabel}
				/>
			)}
			{filtered.map((crewDefinition) => (
				<CrewDefinitionCard
					key={crewDefinition.id}
					crewDefinition={crewDefinition}
				/>
			))}
			{addModal.isOpen && (
				<AddCrewDefinitionModal
					isOpen={addModal.isOpen}
					onClose={addModal.onClose}
				/>
			)}
		</ContentCard>
	);
}
